/**
 * PreApplyRequirementsDialog
 *
 * Pop-up shown to a student/agent before they submit an application to a
 * course. It surfaces the Pakistan-specific admission criteria pulled from
 * `university.pakistan_requirements`, so the applicant can self-check before
 * we accept the application.
 *
 * Falls back to a sensible default block if the university has no enriched
 * data yet.
 */
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, GraduationCap, FileText, Wallet, ShieldAlert, Languages, BadgeCheck } from 'lucide-react';

const Section = ({ icon: Icon, title, children, tone = 'default' }) => (
  <div className={`p-4 rounded-lg border ${tone === 'amber'
    ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
    : 'bg-gray-50 dark:bg-slate-700/50 border-gray-200 dark:border-slate-600'}`}
  >
    <div className="flex items-center gap-2 mb-2">
      <Icon className={`w-4 h-4 ${tone === 'amber' ? 'text-amber-700 dark:text-amber-300' : 'text-teal-600 dark:text-teal-400'}`} />
      <h4 className={`font-semibold text-sm ${tone === 'amber' ? 'text-amber-800 dark:text-amber-200' : 'text-gray-800 dark:text-white'}`}>{title}</h4>
    </div>
    <div className={`text-sm ${tone === 'amber' ? 'text-amber-900 dark:text-amber-100' : 'text-gray-700 dark:text-gray-300'}`}>
      {children}
    </div>
  </div>
);

export const PreApplyRequirementsDialog = ({
  open,
  onOpenChange,
  university,           // { name, country, pakistan_requirements?, min_ielts?, english_requirements?, academic_requirements? }
  course,               // { name, university_name? }
  submitting = false,
  onConfirm,            // fn() — called when applicant confirms they meet the criteria
}) => {
  const pak = university?.pakistan_requirements;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="pre-apply-requirements-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-gray-800 dark:text-white">
            <span className="text-xl">🇵🇰</span>
            Entry Requirements — Pakistani Applicants
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-1 mb-3">
          <p className="text-sm text-gray-500 dark:text-gray-400">You're about to apply to:</p>
          <p className="font-semibold text-gray-800 dark:text-white">{course?.name}</p>
          <p className="text-sm text-gray-600 dark:text-gray-400">{university?.name} · {university?.country}</p>
        </div>

        {pak?.summary && (
          <p className="text-sm italic text-gray-600 dark:text-gray-300 border-l-2 border-teal-500 pl-3 mb-4">
            {pak.summary}
          </p>
        )}

        <div className="space-y-3">
          {/* Academic */}
          <Section icon={GraduationCap} title="Academic">
            {pak?.academic && (pak.academic.undergraduate_min || pak.academic.postgraduate_min || pak.academic.phd_min) ? (
              <ul className="list-disc list-inside space-y-1">
                {pak.academic.undergraduate_min && <li><b>Undergraduate:</b> {pak.academic.undergraduate_min}</li>}
                {pak.academic.postgraduate_min && <li><b>Postgraduate:</b> {pak.academic.postgraduate_min}</li>}
                {pak.academic.phd_min && <li><b>PhD:</b> {pak.academic.phd_min}</li>}
              </ul>
            ) : university?.academic_requirements ? (
              <p>{university.academic_requirements}</p>
            ) : (
              <ul className="list-disc list-inside space-y-1">
                <li>Undergraduate: FSc 60% / A-Levels 3 passes (HEC attested)</li>
                <li>Postgraduate: Bachelor's 55%+ (2:2 equivalent), HEC attested</li>
              </ul>
            )}
          </Section>

          {/* English */}
          <Section icon={Languages} title="English Proficiency">
            {pak?.english && (pak.english.ielts || pak.english.toefl_ibt || pak.english.pte || pak.english.duolingo) ? (
              <ul className="list-disc list-inside space-y-1">
                {pak.english.ielts && <li>IELTS overall <span className="font-mono">{pak.english.ielts}</span>{pak.english.ielts_subscore_min ? <> (min subscore <span className="font-mono">{pak.english.ielts_subscore_min}</span>)</> : null}</li>}
                {pak.english.toefl_ibt && <li>TOEFL iBT <span className="font-mono">{pak.english.toefl_ibt}+</span></li>}
                {pak.english.pte && <li>PTE <span className="font-mono">{pak.english.pte}+</span></li>}
                {pak.english.duolingo && <li>Duolingo <span className="font-mono">{pak.english.duolingo}+</span></li>}
                {pak.english.alternatives && <li className="italic">{pak.english.alternatives}</li>}
              </ul>
            ) : university?.english_requirements ? (
              <p>{university.english_requirements}</p>
            ) : university?.min_ielts ? (
              <p>IELTS overall <span className="font-mono">{university.min_ielts}</span></p>
            ) : (
              <p>IELTS 6.0+ overall (no band &lt; 5.5), or PTE 50+, or institution-approved alternative.</p>
            )}
          </Section>

          {/* Financial */}
          {(pak?.financial) && (
            <Section icon={Wallet} title="Financial Proof">
              <p>{pak.financial}</p>
            </Section>
          )}

          {/* Documents */}
          {pak?.documents?.length > 0 && (
            <Section icon={FileText} title="Required Documents">
              <ul className="list-disc list-inside space-y-1">
                {pak.documents.map((d, i) => (
                  <li key={i}>{d}</li>
                ))}
              </ul>
            </Section>
          )}

          {/* Visa */}
          {pak?.visa && (pak.visa.type || pak.visa.processing_weeks || pak.visa.common_blockers) && (
            <Section icon={ShieldAlert} title="Visa" tone="amber">
              <ul className="list-disc list-inside space-y-1">
                {pak.visa.type && <li><b>Type:</b> {pak.visa.type}</li>}
                {pak.visa.processing_weeks && <li><b>Processing:</b> {pak.visa.processing_weeks} weeks</li>}
                {pak.visa.common_blockers && <li><b>Common blockers:</b> {pak.visa.common_blockers}</li>}
              </ul>
            </Section>
          )}

          {!pak && (
            <p className="text-xs text-gray-500 italic px-1">
              Detailed entry criteria for this university are still being verified by our admissions team.
              Above shows our standard guidance — your counsellor will confirm exact thresholds after you apply.
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 pt-4 border-t border-gray-200 dark:border-slate-700 mt-4">
          <Button variant="outline" onClick={() => onOpenChange?.(false)} disabled={submitting} data-testid="pre-apply-cancel">
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={submitting}
            className="bg-teal-600 hover:bg-teal-700 text-white gap-2"
            data-testid="pre-apply-confirm"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Submitting…
              </>
            ) : (
              <>
                <BadgeCheck className="w-4 h-4" /> I meet these — Apply
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PreApplyRequirementsDialog;
